import React, { useMemo } from 'react';
import type { Role, Comment } from 'types';
import NewComment from 'components/Comments/NewComment/NewComment';
import { checkForLastUnresolvedComment, groupComments } from 'utils/comments';
import { ProjectState } from 'redux/reducers/project';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import {
  ProjectContainer,
  ReviewerCommentTitleWrapper,
} from '../../../elements';
import CommentThread from '../CommentThread/CommentThread';

export type ReviewCommentListProps = {
  userRole: Role;
  user: string;
  comments: Comment[];
  onSave: (comment: Comment) => Promise<boolean>;
  identifier?: string;
  title?: string;
  readOnly?: boolean;
  isWorkStepsResolved: boolean;
  newCommentTitleElement?: () => React.ReactElement;
  secondaryItem?: () => React.ReactElement;
};

export const ReviewCommentList = ({
  title,
  userRole,
  user,
  comments,
  identifier,
  onSave,
  readOnly = false,
  isWorkStepsResolved,
  secondaryItem,
  newCommentTitleElement,
}: ReviewCommentListProps) => {
  const { project } = useSelector<RootState, ProjectState>(
    (state) => state.project
  );
  const groupedCommentsList = useMemo(
    () => groupComments(comments),
    [comments]
  );

  const lastUnresolvedComment = useMemo(() => {
    return checkForLastUnresolvedComment(
      comments,
      groupedCommentsList,
      project
    );
  }, [comments, groupedCommentsList, project.status]);

  const shouldRenderCommentBox = !readOnly && userRole === 'reviewer';

  return (
    <ProjectContainer isNewCommentFlow>
      {title && (
        <ReviewerCommentTitleWrapper>{title}</ReviewerCommentTitleWrapper>
      )}
      {groupedCommentsList.map((comment) => (
        <CommentThread
          key={`comment-${comment.id}`}
          groupedComment={comment}
          userRole={userRole}
          identifier={identifier}
          user={user}
          projectStatus={project.status}
          isWorkStepsResolved={isWorkStepsResolved}
          onReplySave={onSave}
          lastUnresolvedComment={
            lastUnresolvedComment &&
            lastUnresolvedComment[0].id === comment.parent.id
              ? lastUnresolvedComment
              : null
          }
        />
      ))}
      {shouldRenderCommentBox && (
        <NewComment
          identifier={identifier}
          userRole={userRole}
          user={user}
          onSave={onSave}
          secondaryItem={secondaryItem}
          newCommentTitleElement={newCommentTitleElement}
        />
      )}
    </ProjectContainer>
  );
};
